import React, { useState, useRef } from 'react';
import { generateVariations } from '../api';

const STEPS = ['Contatos', 'Mensagem', 'Mídia', 'Revisar', 'Disparar'];

export default function BlastModal({ onClose, instances, companies, companyId }) {
  const companyInstances = instances.filter(i => !companyId || i.company === companyId);
  const company = companies.find(c => c.id === companyId);

  const [step, setStep] = useState(0);
  const [data, setData] = useState({
    selectedGroups: [],
    spreadsheetFile: null,
    contactCount: 0,
    flowType: 'texto',
    selectedInstance: companyInstances[0]?.id || '',
    template: '',
    variations: [],
    agentContext: '',
    mediaFile: null,
    mediaPreview: null,
    mediaUrl: '',
    caption: '',
    captionVariations: [],
    progress: 0,
    sent: 0,
    total: 0,
    status: 'idle',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const fileRef = useRef();
  const mediaRef = useRef();

  const update = patch => setData(d => ({ ...d, ...patch }));

  const instance = companyInstances.find(i => i.id === data.selectedInstance);

  // ── HANDLERS ─────────────────────────────────────────────────────────────

  const handleSheetFile = (file) => {
    update({ spreadsheetFile: file, contactCount: 340 }); // 340 simulado; em prod parseia CSV
  };

  // FIXED: proper file handling with FileReader for preview
  const handleMediaFile = (file) => {
    if (!file) return;
    setError('');
    setLoading(true);

    const reader = new FileReader();
    reader.onload = (e) => {
      update({
        mediaFile: file,
        mediaPreview: e.target.result,
        mediaUrl: e.target.result, // em prod: upload Cloudinary e pega URL
      });
      setLoading(false);
    };
    reader.onerror = () => {
      setError('Erro ao carregar arquivo. Tente novamente.');
      setLoading(false);
    };
    reader.readAsDataURL(file);
  };

  const handleGenerateVars = () => {
    if (!data.template.trim()) { setError('Escreva a mensagem modelo primeiro.'); return; }
    setError('');
    update({ variations: generateVariations(data.template) });
  };

  const handleGenerateCaptionVars = () => {
    if (!data.caption.trim()) { setError('Escreva a legenda primeiro.'); return; }
    setError('');
    update({ captionVariations: generateVariations(data.caption) });
  };

  const handleFire = () => {
    update({ status: 'running', total: data.contactCount || 340, sent: 0, progress: 0 });
    setStep(4);
    const total = data.contactCount || 340;
    let sent = 0;
    const iv = setInterval(() => {
      sent = Math.min(sent + Math.floor(Math.random() * 8) + 2, total);
      update({ sent, progress: Math.round((sent / total) * 100) });
      if (sent >= total) { clearInterval(iv); update({ status: 'done' }); }
    }, 200);
  };

  const canNext = () => {
    if (step === 0) return data.selectedGroups.length > 0 || data.contactCount > 0;
    if (step === 1) return data.template.trim().length > 0 && data.variations.length > 0 && data.selectedInstance;
    if (step === 2) return data.flowType === 'texto' || !!data.mediaUrl;
    if (step === 3) return true;
    return false;
  };

  // ── STYLES ────────────────────────────────────────────────────────────────

  const s = {
    overlay: {
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
    },
    modal: {
      background: '#1c1c1f', border: '1px solid #27272a', borderRadius: 14,
      width: 560, maxHeight: '90vh', display: 'flex', flexDirection: 'column', overflow: 'hidden',
    },
    header: {
      padding: '14px 18px', borderBottom: '1px solid #27272a',
      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    },
    hTitle: { fontSize: 15, fontWeight: 600, color: '#e4e4e7', display: 'flex', alignItems: 'center', gap: 8 },
    hCompany: {
      fontSize: 11, fontWeight: 500, padding: '2px 8px', borderRadius: 20,
      background: (company?.color || '#6366f1') + '22', color: company?.color || '#818cf8',
    },
    closeBtn: { background: 'none', border: 'none', color: '#71717a', fontSize: 18, cursor: 'pointer' },
    stepBar: {
      display: 'flex', alignItems: 'center', padding: '10px 18px',
      borderBottom: '1px solid #27272a', gap: 4,
    },
    stepDot: (state) => ({
      width: 22, height: 22, borderRadius: '50%', flexShrink: 0,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: 10, fontWeight: 700,
      background: state === 'done' ? '#6366f1' : state === 'current' ? '#1e1b4b' : '#18181b',
      color: state === 'done' ? '#fff' : state === 'current' ? '#818cf8' : '#52525b',
      border: state === 'current' ? '1.5px solid #6366f1' : '1px solid #27272a',
    }),
    stepLabel: (state) => ({ fontSize: 10, color: state === 'current' ? '#818cf8' : '#52525b', marginTop: 2 }),
    stepLine: { flex: 1, height: 1, background: '#27272a' },
    body: { padding: 18, overflowY: 'auto', flex: 1, display: 'flex', flexDirection: 'column', gap: 14 },
    footer: {
      padding: '12px 18px', borderTop: '1px solid #27272a',
      display: 'flex', justifyContent: 'flex-end', gap: 8,
    },
    sTitle: { fontSize: 11, fontWeight: 600, color: '#52525b', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 },
    input: {
      width: '100%', padding: '8px 12px', background: '#18181b',
      border: '1px solid #3f3f46', borderRadius: 8, color: '#e4e4e7', fontSize: 13, outline: 'none',
    },
    textarea: {
      width: '100%', padding: '8px 12px', background: '#18181b',
      border: '1px solid #3f3f46', borderRadius: 8, color: '#e4e4e7',
      fontSize: 13, outline: 'none', resize: 'vertical', minHeight: 90,
    },
    select: {
      width: '100%', padding: '8px 12px', background: '#18181b',
      border: '1px solid #3f3f46', borderRadius: 8, color: '#e4e4e7', fontSize: 13, outline: 'none',
    },
    uploadZone: (hasFile) => ({
      border: `1.5px dashed ${hasFile ? '#22c55e' : '#3f3f46'}`,
      background: hasFile ? '#1e3a2f' : 'transparent',
      borderRadius: 10, padding: 20, textAlign: 'center', cursor: 'pointer',
    }),
    uploadIcon: { fontSize: 28, marginBottom: 6 },
    uploadLabel: (hasFile) => ({ fontSize: 13, fontWeight: 500, color: hasFile ? '#22c55e' : '#a1a1aa' }),
    uploadSub: { fontSize: 11, color: '#52525b', marginTop: 2 },
    mediaPreview: { width: '100%', maxHeight: 160, objectFit: 'cover', borderRadius: 8, marginTop: 8 },
    varBox: {
      background: '#18181b', borderLeft: '3px solid #6366f1',
      borderRadius: '0 6px 6px 0', padding: '8px 10px',
      marginBottom: 6, fontSize: 12, color: '#a1a1aa', lineHeight: 1.5,
    },
    varNum: { fontSize: 10, fontWeight: 700, color: '#818cf8', marginBottom: 3 },
    chipGroup: { display: 'flex', flexWrap: 'wrap', gap: 6 },
    chip: (on) => ({
      padding: '4px 10px', borderRadius: 20, fontSize: 11, fontWeight: 500,
      border: `1px solid ${on ? '#6366f1' : '#3f3f46'}`,
      background: on ? '#6366f1' : 'transparent',
      color: on ? '#fff' : '#71717a', cursor: 'pointer',
    }),
    radioRow: { display: 'flex', gap: 8 },
    radioBtn: (a) => ({
      flex: 1, padding: '8px 12px',
      background: a ? 'rgba(99,102,241,0.15)' : '#18181b',
      border: `1px solid ${a ? '#6366f1' : '#3f3f46'}`,
      borderRadius: 8, color: a ? '#818cf8' : '#71717a',
      fontSize: 12, fontWeight: 500, cursor: 'pointer', textAlign: 'center',
    }),
    row: { display: 'flex', justifyContent: 'space-between', fontSize: 12, padding: '5px 0', borderBottom: '1px solid #27272a' },
    rl: { color: '#71717a' },
    rv: { fontWeight: 500, color: '#e4e4e7' },
    progBar: { height: 6, background: '#27272a', borderRadius: 3, overflow: 'hidden', margin: '8px 0' },
    progFill: (pct) => ({ height: '100%', background: '#6366f1', borderRadius: 3, width: pct + '%', transition: 'width 0.3s' }),
    btnPrimary: { padding: '8px 18px', background: '#6366f1', border: 'none', borderRadius: 8, color: '#fff', fontSize: 13, fontWeight: 500, cursor: 'pointer' },
    btnSecondary: { padding: '8px 18px', background: 'transparent', border: '1px solid #3f3f46', borderRadius: 8, color: '#a1a1aa', fontSize: 13, cursor: 'pointer' },
    btnGhost: { padding: '6px 12px', background: 'transparent', border: '1px solid #3f3f46', borderRadius: 6, color: '#a1a1aa', fontSize: 12, cursor: 'pointer' },
    btnFire: { padding: '8px 18px', background: '#dc2626', border: 'none', borderRadius: 8, color: '#fff', fontSize: 13, fontWeight: 500, cursor: 'pointer' },
    errorMsg: { color: '#ef4444', fontSize: 12, marginTop: -8 },
    statRow: { display: 'flex', gap: 8, marginTop: 12 },
    statCard: { flex: 1, background: '#18181b', border: '1px solid #27272a', borderRadius: 8, padding: 10, textAlign: 'center' },
    statNum: { fontSize: 20, fontWeight: 600, color: '#e4e4e7' },
    statLabel: { fontSize: 11, color: '#71717a', marginTop: 2 },
  };

  const GROUPS = companyId === 'perpetue'
    ? ['Leads Sofia', 'Base Perpetue', 'Reengajamento', 'Pós-venda']
    : companyId === 'ioa'
    ? ['Leads IOA', 'Base HofDay', 'Clientes Botox', 'Reengajamento']
    : ['Grupo 1', 'Grupo 2'];

  const toggleGroup = (g) => {
    const sel = data.selectedGroups.includes(g)
      ? data.selectedGroups.filter(x => x !== g)
      : [...data.selectedGroups, g];
    update({ selectedGroups: sel });
  };

  // ── RENDER ────────────────────────────────────────────────────────────────

  const renderStep = () => {
    switch (step) {
      case 0: return (
        <>
          <div>
            <div style={s.sTitle}>Grupos de contatos — {company?.name}</div>
            <div style={s.chipGroup}>
              {GROUPS.map(g => (
                <span key={g} style={s.chip(data.selectedGroups.includes(g))} onClick={() => toggleGroup(g)}>{g}</span>
              ))}
            </div>
          </div>
          <div>
            <div style={s.sTitle}>Ou importar planilha (CSV / XLSX)</div>
            <input type="file" accept=".csv,.xlsx,.xls" ref={fileRef} style={{ display: 'none' }}
              onChange={e => e.target.files[0] && handleSheetFile(e.target.files[0])} />
            <div style={s.uploadZone(!!data.spreadsheetFile)} onClick={() => fileRef.current.click()}>
              <div style={s.uploadIcon}>{data.spreadsheetFile ? '✅' : '📊'}</div>
              <div style={s.uploadLabel(!!data.spreadsheetFile)}>
                {data.spreadsheetFile ? `${data.spreadsheetFile.name} — ${data.contactCount} contatos` : 'Clique para selecionar'}
              </div>
              <div style={s.uploadSub}>Colunas obrigatórias: NOME, WHATSAPP</div>
            </div>
          </div>
          {data.contactCount > 0 && (
            <div style={s.statRow}>
              <div style={s.statCard}><div style={s.statNum}>{data.contactCount}</div><div style={s.statLabel}>Total</div></div>
              <div style={s.statCard}><div style={{ ...s.statNum, color: '#22c55e' }}>{Math.round(data.contactCount * 0.94)}</div><div style={s.statLabel}>Válidos</div></div>
              <div style={s.statCard}><div style={{ ...s.statNum, color: '#ef4444' }}>{Math.round(data.contactCount * 0.06)}</div><div style={s.statLabel}>Inválidos</div></div>
            </div>
          )}
        </>
      );

      case 1: return (
        <>
          <div>
            <div style={s.sTitle}>Tipo de disparo</div>
            <div style={s.radioRow}>
              <button style={s.radioBtn(data.flowType === 'texto')} onClick={() => update({ flowType: 'texto' })}>📝 Só texto</button>
              <button style={s.radioBtn(data.flowType === 'midia')} onClick={() => update({ flowType: 'midia' })}>🖼️ Texto + Mídia</button>
            </div>
          </div>
          <div>
            <div style={s.sTitle}>Instância WhatsApp</div>
            <select style={s.select} value={data.selectedInstance} onChange={e => update({ selectedInstance: e.target.value })}>
              <option value="">Selecionar chip...</option>
              {companyInstances.map(i => <option key={i.id} value={i.id}>{i.name}</option>)}
            </select>
          </div>
          <div>
            <div style={s.sTitle}>Mensagem modelo</div>
            <textarea style={s.textarea}
              placeholder={`Oi, {{nome}}. Tudo bem?\n\nDescreva aqui a mensagem principal...`}
              value={data.template}
              onChange={e => { update({ template: e.target.value }); setError(''); }} />
            <div style={{ fontSize: 11, color: '#52525b', marginTop: 4 }}>Use {'{{nome}}'} para personalizar</div>
          </div>
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <div style={s.sTitle}>5 variações automáticas</div>
              <button style={s.btnGhost} onClick={handleGenerateVars}>⚡ Gerar agora</button>
            </div>
            {error && <div style={s.errorMsg}>{error}</div>}
            {data.variations.length > 0
              ? data.variations.map((v, i) => (
                <div key={i} style={s.varBox}>
                  <div style={s.varNum}>Variação {i + 1}</div>
                  {v.length > 120 ? v.substring(0, 120) + '...' : v}
                </div>
              ))
              : <div style={{ fontSize: 12, color: '#52525b' }}>Escreva a mensagem e clique em "Gerar agora"</div>
            }
          </div>
          <div>
            <div style={s.sTitle}>Contexto para o SDR Agent</div>
            <input style={s.input}
              placeholder="Ex: Sofia já se apresentou e enviou proposta de automação."
              value={data.agentContext}
              onChange={e => update({ agentContext: e.target.value })} />
            <div style={{ fontSize: 11, color: '#52525b', marginTop: 4 }}>
              Gravado no Postgres → SDR Agent usa para continuar a conversa
            </div>
          </div>
        </>
      );

      case 2: return (
        <>
          {data.flowType === 'texto' ? (
            <div style={{ padding: 24, textAlign: 'center', color: '#71717a', fontSize: 13 }}>
              ℹ️ Disparo só texto selecionado. Clique em Próximo.
            </div>
          ) : (
            <>
              <div>
                <div style={s.sTitle}>Imagem ou vídeo curto</div>
                {/* FIXED: hidden input controlled via ref, onChange always fires */}
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/gif,video/mp4"
                  ref={mediaRef}
                  style={{ display: 'none' }}
                  onChange={e => {
                    const file = e.target.files?.[0];
                    if (file) handleMediaFile(file);
                    // Reset input so same file can be selected again
                    e.target.value = '';
                  }}
                />
                <div
                  style={s.uploadZone(!!data.mediaFile)}
                  onClick={() => mediaRef.current && mediaRef.current.click()}
                >
                  {data.mediaPreview && data.mediaFile?.type?.startsWith('image') ? (
                    <img src={data.mediaPreview} alt="preview" style={s.mediaPreview} />
                  ) : (
                    <>
                      <div style={s.uploadIcon}>{loading ? '⏳' : data.mediaFile ? '✅' : '🖼️'}</div>
                      <div style={s.uploadLabel(!!data.mediaFile)}>
                        {loading ? 'Carregando...' : data.mediaFile ? data.mediaFile.name : 'Clique para selecionar (JPG, PNG, MP4)'}
                      </div>
                      <div style={s.uploadSub}>Máx 16MB · será hospedado no Cloudinary</div>
                    </>
                  )}
                </div>
                {error && <div style={s.errorMsg}>{error}</div>}
              </div>
              <div>
                <div style={s.sTitle}>Legenda da mídia</div>
                <textarea style={s.textarea}
                  placeholder="✨ {{nome}}, oferta especial para você..."
                  value={data.caption}
                  onChange={e => update({ caption: e.target.value })} />
              </div>
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <div style={s.sTitle}>Variações da legenda</div>
                  <button style={s.btnGhost} onClick={handleGenerateCaptionVars}>⚡ Gerar 5</button>
                </div>
                {data.captionVariations.map((v, i) => (
                  <div key={i} style={s.varBox}>
                    <div style={s.varNum}>Legenda {i + 1}</div>
                    {v.length > 100 ? v.substring(0, 100) + '...' : v}
                  </div>
                ))}
              </div>
            </>
          )}
        </>
      );

      case 3: return (
        <>
          <div style={{ fontSize: 13, fontWeight: 600, color: '#e4e4e7', marginBottom: 8 }}>Revise antes de disparar</div>
          <div style={{ background: '#18181b', border: '1px solid #27272a', borderRadius: 8, padding: 12 }}>
            <div style={s.row}><span style={s.rl}>Empresa</span><span style={s.rv}>{company?.name}</span></div>
            <div style={s.row}><span style={s.rl}>Contatos</span><span style={s.rv}>{data.contactCount || data.selectedGroups.length * 100 || '—'}</span></div>
            <div style={s.row}><span style={s.rl}>Tipo</span><span style={s.rv}>{data.flowType === 'texto' ? '📝 Só texto' : '🖼️ Texto + Mídia'}</span></div>
            <div style={s.row}><span style={s.rl}>Instância</span><span style={s.rv}>{instance?.name || '—'}</span></div>
            <div style={s.row}><span style={s.rl}>Variações</span><span style={s.rv}>{data.variations.length}</span></div>
            <div style={s.row}><span style={s.rl}>Mídia</span><span style={s.rv}>{data.mediaFile ? `✓ ${data.mediaFile.name}` : '—'}</span></div>
            <div style={{ ...s.row, borderBottom: 'none' }}><span style={s.rl}>Intervalo anti-ban</span><span style={s.rv}>Aleatório 30–90s</span></div>
          </div>
          <div style={{ background: '#1e1b4b', border: '1px solid #3730a3', borderRadius: 8, padding: 10, fontSize: 12, color: '#c7d2fe' }}>
            ⚠️ O app vai atualizar o Code Node "Mensagem" no fluxo n8n com as variações, depois ativa o workflow.
          </div>
        </>
      );

      case 4: return (
        <>
          <div style={{ textAlign: 'center', fontSize: 34, marginBottom: 8 }}>
            {data.status === 'done' ? '✅' : '🚀'}
          </div>
          <div style={{ textAlign: 'center', fontSize: 15, fontWeight: 600, color: '#e4e4e7', marginBottom: 4 }}>
            {data.status === 'done' ? 'Disparo concluído!' : 'Disparando...'}
          </div>
          <div style={{ textAlign: 'center', fontSize: 13, color: '#71717a', marginBottom: 12 }}>
            {data.status === 'done' ? `${data.sent} mensagens enviadas` : 'Fluxo n8n em execução'}
          </div>
          <div style={s.progBar}><div style={s.progFill(data.progress)} /></div>
          <div style={s.statRow}>
            <div style={s.statCard}><div style={s.statNum}>{data.sent}</div><div style={s.statLabel}>Enviados</div></div>
            <div style={s.statCard}><div style={{ ...s.statNum, color: '#22c55e' }}>{Math.round(data.sent * 0.94)}</div><div style={s.statLabel}>Entregues</div></div>
            <div style={s.statCard}><div style={{ ...s.statNum, color: '#ef4444' }}>{Math.round(data.sent * 0.02)}</div><div style={s.statLabel}>Erros</div></div>
          </div>
          <div style={{ marginTop: 14, fontSize: 11, color: '#52525b' }}>
            <div style={{ fontWeight: 600, color: '#71717a', marginBottom: 6 }}>Nodes n8n em execução</div>
            {['Sheets → lendo contatos', 'Code Node → variações aleatórias', `${instance?.name} → enviando`, 'Sheets → registrando STATUS', 'Postgres → memória inicial SDR'].map((n, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                <span style={{ width: 6, height: 6, borderRadius: '50%', background: data.status === 'done' || i < 3 ? '#22c55e' : '#3f3f46', flexShrink: 0, display: 'inline-block' }} />
                {n}
              </div>
            ))}
          </div>
        </>
      );

      default: return null;
    }
  };

  return (
    <div style={s.overlay} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={s.modal}>
        <div style={s.header}>
          <div style={s.hTitle}>
            🚀 Disparador em Massa
            {company && <span style={s.hCompany}>{company.name}</span>}
          </div>
          <button style={s.closeBtn} onClick={onClose}>✕</button>
        </div>

        <div style={s.stepBar}>
          {STEPS.map((label, i) => {
            const state = i < step ? 'done' : i === step ? 'current' : 'pending';
            return (
              <React.Fragment key={i}>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                  <div style={s.stepDot(state)}>{i < step ? '✓' : i + 1}</div>
                  <span style={s.stepLabel(state)}>{label}</span>
                </div>
                {i < STEPS.length - 1 && <div style={s.stepLine} />}
              </React.Fragment>
            );
          })}
        </div>

        <div style={s.body}>{renderStep()}</div>

        <div style={s.footer}>
          {step > 0 && step < 4 && (
            <button style={s.btnSecondary} onClick={() => { setStep(s => s - 1); setError(''); }}>← Voltar</button>
          )}
          {step < 3 && (
            <button
              style={{ ...s.btnPrimary, opacity: canNext() ? 1 : 0.5 }}
              onClick={() => { if (canNext()) { setError(''); setStep(s => s + 1); } }}
            >
              Próximo →
            </button>
          )}
          {step === 3 && (
            <button style={s.btnFire} onClick={handleFire}>🚀 Disparar agora</button>
          )}
          {step === 4 && data.status === 'done' && (
            <button style={s.btnPrimary} onClick={onClose}>Fechar</button>
          )}
        </div>
      </div>
    </div>
  );
}
